import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../design-system/form/form-primitives';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { cn } from '../../../../utils/cn';

type BeneficiarySelectFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  form: UseFormReturn<TFieldValues>;
  label: string;
  badge?: string;
  inputName: TName;
  placeholder?: string;
  onChange?: (value: TFieldValues[TName]) => void;
  className?: string;
  children: React.ReactNode;
};

export const BeneficiarySelectField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  form,
  label,
  badge,
  inputName,
  placeholder,
  onChange,
  className,
  children,
}: BeneficiarySelectFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={form.control}
      name={inputName}
      render={({ field, formState }) => {
        return (
          <FormItem onBlur={field.onBlur}>
            <Stack space={1} align="stretch" className="relative">
              {badge && <p className="absolute top-1 right-3 text-[10px] font-medium text-muted-foreground">{badge}</p>}

              <FormControl>
                <Select
                  aria-label={label}
                  required
                  placeholder={placeholder}
                  value={field.value}
                  onValueChange={(value: TFieldValues[TName]) => {
                    field.onChange(value);

                    onChange?.(value);
                  }}
                  triggerLabel={<FormLabel className="text-xs font-medium text-muted-foreground">{label}</FormLabel>}
                  triggerClassName={cn(
                    'rounded-lg border border-border bg-white px-4 py-3 text-base font-medium text-black [&_.account-logo]:h-4 [&_.account-logo]:w-4 [&_.account-type]:hidden',
                    { 'border-destructive': !!formState.errors.country },
                    className,
                  )}
                >
                  {children}
                </Select>
              </FormControl>

              <FormMessage className="w-full text-right" />
            </Stack>
          </FormItem>
        );
      }}
    />
  );
};
